import type { AdminUserKycStatus, AdminUserRole, AdminUserStatus } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Status / role / KYC badge chips for the users list + detail drawer
 * (AUT-124). Colours mirror the mockup's `badge--{success,warning,danger,
 * info,neutral}` classes via MUI Chip `color`. Labels resolve through
 * i18n (CLAUDE.md rule #11).
 */

type ChipColor = ChipProps['color'];

const STATUS_COLOR: Record<AdminUserStatus, ChipColor> = {
  active: 'success',
  suspended: 'warning',
  banned: 'error',
  deleted: 'default',
};

const ROLE_COLOR: Record<AdminUserRole, ChipColor> = {
  customer: 'default',
  inspector: 'info',
  admin: 'secondary',
};

/** KYC pipeline status → chip colour. Terminal-pass = success, etc. */
const KYC_COLOR: Record<NonNullable<AdminUserKycStatus>, ChipColor> = {
  unverified: 'default',
  profile_filled: 'default',
  idcheck_pending: 'warning',
  idcheck_passed: 'success',
  idcheck_failed: 'error',
  bank_pending: 'warning',
  active: 'success',
  reverification_required: 'warning',
  suspended: 'warning',
  banned: 'error',
};

export const StatusBadge = ({ status }: { status: AdminUserStatus }): ReactElement => {
  const { t } = useTranslation();
  return <Chip size="small" color={STATUS_COLOR[status]} label={t(`users.status.${status}`)} />;
};

export const RoleBadge = ({ role }: { role: AdminUserRole }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      variant="outlined"
      color={ROLE_COLOR[role]}
      label={t(`users.role.${role}`)}
    />
  );
};

export const KycBadge = ({ kycStatus }: { kycStatus: AdminUserKycStatus }): ReactElement => {
  const { t } = useTranslation();
  if (kycStatus === null) {
    return <Chip size="small" color="default" label={t('users.kyc.none')} />;
  }
  return <Chip size="small" color={KYC_COLOR[kycStatus]} label={t(`users.kyc.${kycStatus}`)} />;
};
